import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Eye, EyeOff, Plus, RefreshCcw } from "lucide-react";

// === Import your custom components ===
import Header from "../components/Header";
import BlueButton from "../components/BlueButton";
import SimpleTable from "../components/SimpleTable";
import Card from "../components/Card";
import Badge from "../components/Badge";
import RetailerForm from "../components/RetailerForm";
import { useNavigate } from "react-router-dom";

export default function Retailers() {
  const [allRetailers, setAllRetailers] = useState([]); // for search
  const [list, setList] = useState([]); // current page
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [filter, setFilter] = useState("");
    
  const [showPwd, setShowPwd] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // active states map { ID: true/false }
  const [activeStates, setActiveStates] = useState({});

  // update password
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState(null);

  // assign Key
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignRetailer, setAssignRetailer] = useState(null);
  const navigate = useNavigate();

  const authUserString = sessionStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);
  const loginUserID = authUser?.id;
  console.log("senior id:", loginUserID);

  // === Fetch retailers with pagination ===
  
    const fetchRetailers = async () => {
      if (!loginUserID) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `https://goelectronix.in/api/v1/get-allretailers2?page=${page}&per_page=10&distributortID=${loginUserID}`
        );

        const fetchedCustomers = res.data.data.data || [];
        setList(fetchedCustomers);
        setLastPage(res.data.data.last_page || 1);

        // ✅ Initialize active states from fetched data
        const initialStates = {};
        fetchedCustomers.forEach((customer, index) => {
          if (
            customer &&
            customer.ID !== undefined &&
            customer.hasOwnProperty("IS_Active")
          ) {
            initialStates[customer.ID] = customer.IS_Active === 1;
          } else {
            console.warn(`Customer at index ${index} is invalid:`, customer);
          }
        });
        setActiveStates(initialStates);
      } catch (err) {
        console.error("Error fetching retailers:", err);
      } finally {
        setLoading(false);
      }
    };
    useEffect(() => {
      fetchRetailers();
    }, [loginUserID, page]);

  useEffect(() => {
  const fetchAll = async () => {
    if (!loginUserID) return;
    try {
      let all = [];
      let pageNo = 1;
      let lastPage = 1;

      // 🔄 Loop through all pages
      while (pageNo <= lastPage) {
        const res = await axios.get(
          `https://goelectronix.in/api/v1/get-allretailers2?page=${pageNo}&per_page=10&distributortID=${loginUserID}`
        );

        const data = res?.data?.data;
        if (!data) break;

        all = [...all, ...(data.data || [])];
        lastPage = data.last_page || 1;
        pageNo++;
      }

      setAllRetailers(all);

      // ✅ Build activeStates once
      const initialStates = {};
      all.forEach((customer, index) => {
        if (customer?.ID !== undefined && "IS_Active" in customer) {
          initialStates[customer.ID] = customer.IS_Active === 1;
        } else {
          console.warn(`Customer at index ${index} is invalid:`, customer);
        }
      });
      setActiveStates(initialStates);
    } catch (err) {
      console.error("Error fetching all retailers:", err);
    }
  };

  fetchAll();
}, [loginUserID]);


  // === Filtered search (client-side) ===
  const filtered = useMemo(() => {
  if (!filter) return list; // if no search → show paginated data
  const term = filter.toLowerCase();
  return allRetailers.filter(
    (r) =>
      (r.Shop_Name && r.Shop_Name.toLowerCase().includes(term)) ||
      (r.RetailerCode && r.RetailerCode.toLowerCase().includes(term))
  );
}, [filter, list, allRetailers]);



  // === Confirm & toggle active/inactive ===
  // === Confirm & toggle active/inactive ===
  const handleToggleActiveConfirmation = async (customer) => {
  const { ID, Shop_Name } = customer;
  const newActiveState = !activeStates[ID]; // toggled state
  const action = newActiveState ? "activate" : "deactivate"; // fixed

  Swal.fire({
    title: "Are you sure?",
    text: `Do you want to ${action} ${Shop_Name}?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: `Yes, ${action} it!`,
    cancelButtonText: "Cancel",
    buttonsStyling: false,
    customClass: {
      popup: "rounded-2xl shadow-lg p-6 bg-white",
      title: "text-lg font-semibold text-gray-900",
      content: "text-sm text-gray-600",
      confirmButton:
        "bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg",
      cancelButton:
        "bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2 rounded-lg",
    },
  }).then((result) => {
    if (result.isConfirmed) {
      handleToggleActive(customer, newActiveState);
    }
  });
};


  const handleToggleActive = async (customer, newActiveState) => {
    const { ID, Shop_Name } = customer;

    // Optimistic update
    setActiveStates((prev) => ({
      ...prev,
      [ID]: newActiveState,
    }));

    try {
      const apiUrl = `https://goelectronix.in/api/v1/update-retailer/${ID}`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          IS_Active: newActiveState ? 1 : 0, // send numeric value
        }),
      });

      if (!response.ok) {
        // revert if failed
        setActiveStates((prev) => ({
          ...prev,
          [ID]: !newActiveState,
        }));

        const errorData = await response.json();
        Swal.fire({
          title: "Error",
          text: `Failed to update status: ${errorData?.message || response.statusText}`,
          icon: "error",
          buttonsStyling: false,
          customClass: {
            popup: "rounded-2xl shadow-lg p-6 bg-white",
            title: "text-lg font-semibold text-gray-900",
            content: "text-sm text-gray-600",
            confirmButton: "bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg",
          },
        });
        return;
      }

      // ✅ Success alert
      Swal.fire({
        title: newActiveState ? "Activated" : "Deactivated",
        text: `Retailer ${Shop_Name} status changed to ${newActiveState ? "active" : "inactive"}.`,
        icon: "success",
        buttonsStyling: false,
        customClass: {
          popup: "rounded-2xl shadow-lg p-6 bg-white",
          title: "text-lg font-semibold text-gray-900",
          content: "text-sm text-gray-600",
          confirmButton: "bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg",
        },
      });
    } catch (error) {
      // revert on error
      setActiveStates((prev) => ({
        ...prev,
        [ID]: !newActiveState,
      }));

      Swal.fire({
        title: "Error",
        text: `Failed to update status: ${error.message}`,
        icon: "error",
        buttonsStyling: false,
        customClass: {
          popup: "rounded-2xl shadow-lg p-6 bg-white",
          title: "text-lg font-semibold text-gray-900",
          content: "text-sm text-gray-600",
          confirmButton: "bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg",
        },
      });
    }
  };


  return (
    <div className="space-y-3 sm:space-y-4">
      <Header title="Retailers">
  <div className="flex flex-row sm:flex-row sm:items-center sm:gap-2 gap-2 w-full">
    {/* Search Input */}
    <input
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
      placeholder="Search by Retailer Code or Shop Name"
      className="w-full sm:w-1/2 rounded-lg border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />

    {/* Buttons */}
    <div className="flex items-center gap-2 w-full sm:w-auto">
      <BlueButton
        onClick={() => setShowAdd(true)}
        className="inline-flex items-center gap-1 px-3 py-1 sm:px-4 sm:py-2 whitespace-nowrap"
      >
        <Plus size={14} />
        <span className="hidden sm:inline">Add Retailer</span>
      </BlueButton>

      <BlueButton
        onClick={fetchRetailers}
        className="flex items-center justify-center gap-1 w-full sm:w-auto"
      >
        <RefreshCcw size={14} />
        <span className="hidden sm:inline">Refresh</span>
      </BlueButton>
    </div>
  </div>
</Header>


      {loading ? (
        <p className="text-center text-gray-500">Loading retailers...</p>
      ) : (
        <>
          <SimpleTable
            headers={[
              "Sr No.",
              "Retailer (Code)",
              "District",
              "Mobile No",
              "Enterprise",
              "Status",
              "Key Balance",
              "Created At",
              "Actions",
            ]}
            rows={filtered.map((r, index) => [
              <span key={`sr-${r.ID}`}>{index + 1}</span>,

              <div key={r.ID} className="font-medium">
                {r.Shop_Name}{" "}
                <span className="text-xs text-slate-500">
                  ({r.RetailerCode})
                </span>
              </div>,

              <span key={`d-${r.ID}`}>{r.District || "-"}</span>,
              <span key={`m-${r.ID}`}>{r.MobileNo || "-"}</span>,
              <span key={`e-${r.ID}`}>
                {r.EnterpriseStatus === 1 ? "Created" : "Not Created"}
              </span>,

              <Badge
                key={`b-${r.ID}`}
                label={activeStates[r.ID] ? "Active" : "Inactive"}
                color={activeStates[r.ID] ? "blue" : "red"}
              />,

              (() => {
                if (r.smartPolicy && r.regularPolicy) {
                  return (
                    <div key={`p-${r.ID}`}>
                      <span className="block text-sm">
                        Smart = {r.smartPolicy}
                      </span>
                      <span className="block text-sm">
                        Regular = {r.regularPolicy}
                      </span>
                    </div>
                  );
                } else if (r.smartPolicy) {
                  return (
                    <span key={`p-${r.ID}`}>Smart = {r.smartPolicy}</span>
                  );
                } else if (r.regularPolicy) {
                  return (
                    <span key={`p-${r.ID}`}>Regular = {r.regularPolicy}</span>
                  );
                } else {
                  return <span key={`p-${r.ID}`}>0</span>;
                }
              })(),

              new Date(r.CreatedOn).toLocaleDateString(),

              <div className="flex flex-wrap gap-2" key={`act-${r.ID}`}>

                <BlueButton
                  onClick={() => navigate(`/dashboard/user/${r.USerID}`)}
                  className="inline-flex items-center gap-1"
                >
                  <Eye size={14} />
                  <span className="hidden sm:inline">View</span>
                </BlueButton>

                <BlueButton onClick={() => handleToggleActiveConfirmation(r)}>
                  {activeStates[r.ID] ? "Deactivate" : "Activate"}
                </BlueButton>

              </div>,
            ])}
          />

          {/* Pagination Controls */}
          {!filter && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1 rounded border"
              >
                Prev
              </button>
              <span>{page} / {lastPage}</span>
              <button
                disabled={page === lastPage}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 rounded border"
              >
                Next
              </button>
            </div>
          )}

        </>
      )}

      {/* Password Modal */}
      {showPwdModal && selectedRetailer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">
                Update Password - {selectedRetailer.Shop_Name} (
                {selectedRetailer.RetailerCode})
              </h3>
              <button
                onClick={() => setShowPwdModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <input
                value={selectedRetailer.ID}
                readOnly
                className="rounded-lg border px-3 py-2 text-sm bg-gray-100"
              />
              <input
                value={selectedRetailer.Shop_Name}
                readOnly
                className="rounded-lg border px-3 py-2 text-sm bg-gray-100"
              />
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  placeholder="New Password"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                />
                <button
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-2 top-2.5 text-slate-500"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <BlueButton className="sm:col-span-3">Update Password</BlueButton>
            </div>
          </div>
        </div>
      )}

      {/* Add Retailer Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">
                Add Retailer
              </h3>
              <button
                onClick={() => setShowAdd(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <RetailerForm
              onCancel={() => setShowAdd(false)}
              onSubmit={() => setShowAdd(false)}
            />
          </div>
        </div>
      )}

      {/* Assign Keys Modal */}
      {showAssignModal && assignRetailer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">
                Assign Keys - {assignRetailer.Shop_Name} (
                {assignRetailer.RetailerCode})
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <Card title="Assign Keys to Retailer">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  value={assignRetailer.RetailerCode || assignRetailer.ID}
                  readOnly
                  className="rounded-lg border px-3 py-2 text-sm bg-gray-100 w-full"
                />
                <input
                  value={assignRetailer.Shop_Name}
                  readOnly
                  className="rounded-lg border px-3 py-2 text-sm bg-gray-100 w-full"
                />
                <select className="rounded-lg border px-3 py-2 text-sm w-full">
                  <option value="regular">Regular</option>
                  <option value="smart">Smart</option>
                </select>
                <select className="rounded-lg border px-3 py-2 text-sm w-full">
                  <option>Regular Key</option>
                  <option>Regular + Warranty</option>
                  <option>Smart Key</option>
                </select>
                <input
                  placeholder="Start Key (e.g., GE-1000)"
                  className="rounded-lg border px-3 py-2 text-sm w-full"
                />
                <input
                  placeholder="End Key (e.g., GE-1050)"
                  className="rounded-lg border px-3 py-2 text-sm w-full"
                />
                <div className="sm:col-span-2 flex justify-end">
                  <BlueButton>Assign</BlueButton>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}


// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import { Eye, EyeOff, Plus } from "lucide-react";

// // === Import your custom components ===
// import Header from "../components/Header";
// import BlueButton from "../components/BlueButton";
// import SimpleTable from "../components/SimpleTable";
// import Card from "../components/Card";
// import Badge from "../components/Badge";
// import RetailerForm from "../components/RetailerForm";

// export default function Retailers() {
//   const [filter, setFilter] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [showAdd, setShowAdd] = useState(false);
//   const [list, setList] = useState([]);
//   const [loading, setLoading] = useState(false);

//   // pagination state
//   const [page, setPage] = useState(1);
//   const [lastPage, setLastPage] = useState(1);

//   // update password
//   const [showPwdModal, setShowPwdModal] = useState(false);
//   const [selectedRetailer, setSelectedRetailer] = useState(null);

//   // assign Key
//   const [showAssignModal, setShowAssignModal] = useState(false);
//   const [assignRetailer, setAssignRetailer] = useState(null);

//   const authUserString = sessionStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const loginUserID = authUser?.id;
//   console.log("senior id:", loginUserID);

//   // === Fetch retailers with pagination ===
//   useEffect(() => {
//     const fetchRetailers = async () => {
//       if (!loginUserID) return;
//       setLoading(true);
//       try {
//         const res = await axios.get(
//           `https://goelectronix.in/api/v1/get-allretailers2?page=${page}&per_page=10&Senior_id=${loginUserID}`
//         );

//         // API should return { data: [...], last_page: N, current_page: X }
//         setList(res.data.data.data || []);
//         setLastPage(res.data.data.last_page || 1);
//       } catch (err) {
//         console.error("Error fetching retailers:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchRetailers();
//   }, [loginUserID, page]);

//   // === Filtered search (client-side) ===
//   const filtered = useMemo(
//     () =>
//       list.filter((r) =>
//         [r.Shop_Name, r.RetailerCode, r.id]
//           .join(" ")
//           .toLowerCase()
//           .includes(filter.toLowerCase())
//       ),
//     [filter, list]
//   );

//   // === Toggle active/inactive ===
//   const toggleStatus = (id) => {
//     setList((prev) =>
//       prev.map((r) =>
//         r.id === id ? { ...r, IS_Active: r.IS_Active === 1 ? 0 : 1 } : r
//       )
//     );
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Retailers">
//         <div className="flex items-center gap-2">
//           <input
//             value={filter}
//             onChange={(e) => setFilter(e.target.value)}
//             placeholder="Search by name/code/id"
//             className="rounded-lg border px-2 py-1 text-sm"
//           />
//           <BlueButton onClick={() => setShowAdd(true)}>
//             <Plus className="mr-1 inline" size={14} /> Add Retailer
//           </BlueButton>
//         </div>
//       </Header>

//       {loading ? (
//         <p className="text-center text-gray-500">Loading retailers...</p>
//       ) : (
//         <>
//           <SimpleTable
//             headers={[
//               "Sr No.",
//               "Retailer (Code)",
//               "District",
//               "Mobile No",
//               "Enterprise",
//               "Status",
//               "Key Balance",
//               "Created At",
//               "Actions",
//             ]}
//             rows={filtered.map((r, index) => [
//               <span key={`sr-${r.id}`}>{index + 1}</span>,
//               // Retailer (Code)
//               <div key={r.id} className="font-medium">
//                 {r.Shop_Name}{" "}
//                 <span className="text-xs text-slate-500">
//                   ({r.RetailerCode})
//                 </span>
//               </div>,

//               // District
//               <span key={`d-${r.id}`}>{r.District || "-"}</span>,

//               // Mobile No
//               <span key={`m-${r.id}`}>{r.MobileNo || "-"}</span>,

//               // Enterprise
//               <span key={`e-${r.id}`}>
//                 {r.EnterpriseStatus === 1 ? "Created" : "Not Created"}
//               </span>,

//               // Status
//               <Badge
//                 key={`b-${r.id}`}
//                 label={r.IS_Active === 1 ? "Active" : "Inactive"}
//                 color={r.IS_Active === 1 ? "blue" : "red"}
//               />,

//               // Key Balance (Smart / Regular policies)
//               (() => {
//                 if (r.smartPolicy && r.regularPolicy) {
//                   return (
//                     <div key={`p-${r.id}`}>
//                       <span className="block text-sm">Smart = {r.smartPolicy}</span>
//                       <span className="block text-sm">Regular = {r.regularPolicy}</span>
//                     </div>
//                   );
//                 } else if (r.smartPolicy) {
//                   return <span key={`p-${r.id}`}>Smart = {r.smartPolicy}</span>;
//                 } else if (r.regularPolicy) {
//                   return <span key={`p-${r.id}`}>Regular = {r.regularPolicy}</span>;
//                 } else {
//                   return <span key={`p-${r.id}`}>0</span>;
//                 }
//               })(),

//               // Created At
//               new Date(r.CreatedOn).toLocaleDateString(),

//               // Actions
//               <div className="flex flex-wrap gap-2" key={`act-${r.id}`}>
//                 <BlueButton onClick={() => toggleStatus(r.id)}>
//                   {r.IS_Active === 1 ? "Deactivate" : "Activate"}
//                 </BlueButton>
//                 {/* <BlueButton
//                   onClick={() => {
//                     setAssignRetailer(r);
//                     setShowAssignModal(true);
//                   }}
//                 >
//                   Assign Keys
//                 </BlueButton> */}

//                 {/* <BlueButton
//                   onClick={() => {
//                     setSelectedRetailer(r);
//                     setShowPwdModal(true);
//                   }}
//                 >
//                   Update Password
//                 </BlueButton> */}
//               </div>,
//             ])}
//           />


//           {/* === Pagination Controls === */}
//           <div className="flex justify-center gap-2 mt-4">
//             <button
//               disabled={page === 1}
//               onClick={() => setPage((p) => Math.max(1, p - 1))}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Prev
//             </button>
//             <span className="px-3 py-1">
//               Page {page} of {lastPage}
//             </span>
//             <button
//               disabled={page === lastPage}
//               onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
//               className="px-3 py-1 border rounded disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         </>
//       )}

//       {showPwdModal && selectedRetailer && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
//             {/* Modal Header */}
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Update Password - {selectedRetailer.Shop_Name} ({selectedRetailer.RetailerCode})
//               </h3>
//               <button
//                 onClick={() => setShowPwdModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             {/* Form */}
//             <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
//               <input
//                 value={selectedRetailer.ID}
//                 readOnly
//                 className="rounded-lg border px-3 py-2 text-sm bg-gray-100"
//                 placeholder="Retailer ID"
//               />
//               <input
//                 value={selectedRetailer.Shop_Name}
//                 readOnly
//                 className="rounded-lg border px-3 py-2 text-sm bg-gray-100"
//                 placeholder="Username"
//               />
//               <div className="relative">
//                 <input
//                   type={showPwd ? "text" : "password"}
//                   placeholder="New Password"
//                   className="w-full rounded-lg border px-3 py-2 text-sm"
//                 />
//                 <button
//                   onClick={() => setShowPwd((s) => !s)}
//                   className="absolute right-2 top-2.5 text-slate-500"
//                 >
//                   {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//                 </button>
//               </div>
//               <BlueButton className="sm:col-span-3">Update Password</BlueButton>
//             </div>
//           </div>
//         </div>
//       )}


//       {showAdd && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           {/* Modal content */}
//           <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
//             {/* Modal Header */}
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">Add Retailer</h3>
//               <button
//                 onClick={() => setShowAdd(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             {/* Form */}
//             <RetailerForm
//               onCancel={() => setShowAdd(false)}
//               onSubmit={() => setShowAdd(false)}
//             />
//           </div>
//         </div>
//       )}

//       {showAssignModal && assignRetailer && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
//             {/* Modal Header */}
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Assign Keys - {assignRetailer.Shop_Name} ({assignRetailer.RetailerCode})
//               </h3>
//               <button
//                 onClick={() => setShowAssignModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             {/* Assign Keys Form */}
//             <Card title="Assign Keys to Retailer">
//               <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
//                 {/* Prefilled Retailer ID / Code */}
//                 <input
//                   value={assignRetailer.RetailerCode || assignRetailer.id}
//                   readOnly
//                   className="rounded-lg border px-3 py-2 text-sm bg-gray-100 w-full"
//                   placeholder="Retailer ID / Code"
//                 />

//                 {/* Retailer Name */}
//                 <input
//                   value={assignRetailer.Shop_Name}
//                   readOnly
//                   className="rounded-lg border px-3 py-2 text-sm bg-gray-100 w-full"
//                   placeholder="Retailer Name"
//                 />

//                 {/* Key type */}
//                 <select className="rounded-lg border px-3 py-2 text-sm w-full">
//                   <option value="regular">Regular</option>
//                   <option value="smart">Smart</option>
//                 </select>

//                 {/* Key package */}
//                 <select className="rounded-lg border px-3 py-2 text-sm w-full">
//                   <option>Regular Key</option>
//                   <option>Regular + Warranty</option>
//                   <option>Smart Key</option>
//                 </select>

//                 {/* Start Key */}
//                 <input
//                   placeholder="Start Key (e.g., GE-1000)"
//                   className="rounded-lg border px-3 py-2 text-sm w-full"
//                 />

//                 {/* End Key */}
//                 <input
//                   placeholder="End Key (e.g., GE-1050)"
//                   className="rounded-lg border px-3 py-2 text-sm w-full"
//                 />

//                 {/* Submit button - spans full width */}
//                 <div className="sm:col-span-2 flex justify-end">
//                   <BlueButton>Assign</BlueButton>
//                 </div>
//               </div>
//             </Card>

//           </div>
//         </div>
//       )}


//     </div>
//   );
// }



// import React, { useState, useMemo, useEffect } from "react";
// import axios from "axios";
// import { Eye, EyeOff, Plus } from "lucide-react";

// // === Import your custom components ===
// import Header from "../components/Header";
// import BlueButton from "../components/BlueButton";
// import SimpleTable from "../components/SimpleTable";
// import Card from "../components/Card";
// import Badge from "../components/Badge";
// import RetailerForm from "../components/RetailerForm";

// export default function Retailers() {
//   const [filter, setFilter] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [showAdd, setShowAdd] = useState(false);
//   const [list, setList] = useState([]);
//   const [loading, setLoading] = useState(false);

//   // 🔹 Fetch ID from session (assuming you stored it earlier as "id")
//   const seniorId = sessionStorage.getItem("id");
//   console.log("senior id:",seniorId)

//   const authUserString = sessionStorage.getItem("authUserData");
//       const authUser = JSON.parse(authUserString);

//       const loginUserID = authUser.id;
//       console.log('senior id:',loginUserID)


//   // === Fetch retailers from API ===
//   // useEffect(() => {
//   //   const fetchRetailers = async () => {
//   //     if (!loginUserID) return;
//   //     setLoading(true);
//   //     try {
//   //       const res = await axios.get(
//   //         `https://goelectronix.in/api/v1/org_user?role=5&senior_id=${loginUserID}`
//   //       );
//   //       setList(res.data || []); // API returns { data: [...] }
//   //     } catch (err) {
//   //       console.error("Error fetching retailers:", err);
//   //     } finally {
//   //       setLoading(false);
//   //     }
//   //   };

//   //   fetchRetailers();
//   // }, [loginUserID]);

//   useEffect(() => {
//   const fetchRetailers = async () => {
//     if (!loginUserID) return;
//     setLoading(true);
//     try {
//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/get-allretailers2?page=1&per_page=1&Senior_id=${loginUserID}`
//       );
//       setList(res.data.data || []); // ✅ Fix here
//     } catch (err) {
//       console.error("Error fetching retailers:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   fetchRetailers();
// }, [loginUserID]);


//   // === Filtered search ===
//   const filtered = useMemo(
//     () =>
//       list.filter((r) =>
//         [r.business_name, r.org_code, r.id]
//           .join(" ")
//           .toLowerCase()
//           .includes(filter.toLowerCase())
//       ),
//     [filter, list]
//   );

//   // === Toggle active/inactive ===
//   const toggleStatus = (id) => {
//     setList((prev) =>
//       prev.map((r) =>
//         r.id === id
//           ? { ...r, is_active: r.is_active === 1 ? 0 : 1 }
//           : r
//       )
//     );
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Retailers">
//         <div className="flex items-center gap-2">
//           <input
//             value={filter}
//             onChange={(e) => setFilter(e.target.value)}
//             placeholder="Search by name/code/id"
//             className="rounded-lg border px-2 py-1 text-sm"
//           />
//           <BlueButton onClick={() => setShowAdd(true)}>
//             <Plus className="mr-1 inline" size={14} /> Add Retailer
//           </BlueButton>
//         </div>
//       </Header>

//       {loading ? (
//         <p className="text-center text-gray-500">Loading retailers...</p>
//       ) : (
//         <SimpleTable
//           headers={["Retailer (Code)", "Status", "Balance", "Created At", "Actions"]}
//           rows={filtered.map((r) => [
//             <div key={r.id} className="font-medium">
//               {r.Shop_Name}{" "}
//               <span className="text-xs text-slate-500">
//                 ({r.RetailerCode})
//               </span>
//             </div>,
//             <Badge
//               key={`b-${r.id}`}
//               label={r.IS_Active === 1 ? "Active" : "Inactive"}
//               color={r.IS_Active === 1 ? "blue" : "red"}
//             />,
//             r.smartPolicy,
//             new Date(r.CreatedOn).toLocaleDateString(),
//             <div className="flex flex-wrap gap-2" key={`act-${r.id}`}>
//               <BlueButton onClick={() => toggleStatus(r.id)}>
//                 {r.IS_Active === 1 ? "Deactivate" : "Activate"}
//               </BlueButton>
//               <BlueButton>Assign Keys</BlueButton>
//             </div>,
//           ])}
//         />
//       )}

//       <Card title="Login / Password">
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
//           <input placeholder="Retailer ID" className="rounded-lg border px-3 py-2 text-sm" />
//           <input placeholder="Username" className="rounded-lg border px-3 py-2 text-sm" />
//           <div className="relative">
//             <input
//               type={showPwd ? "text" : "password"}
//               placeholder="New Password"
//               className="w-full rounded-lg border px-3 py-2 text-sm"
//             />
//             <button
//               onClick={() => setShowPwd((s) => !s)}
//               className="absolute right-2 top-2.5 text-slate-500"
//             >
//               {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//             </button>
//           </div>
//           <BlueButton className="sm:col-span-3">Update Password</BlueButton>
//         </div>
//       </Card>

//       {showAdd && (
//         <div className="rounded-2xl border bg-white p-4 shadow-xl">
//           <h3 className="text-sm font-semibold" style={{ color: "#0A3E91" }}>
//             Add Retailer
//           </h3>
//           <RetailerForm onCancel={() => setShowAdd(false)} onSubmit={() => {}} />
//         </div>
//       )}
//     </div>
//   );
// }


// import React, { useState, useMemo } from "react";
// // Icons (replace with lucide-react if installed)
// import { Eye, EyeOff, Plus } from "lucide-react";

// // === Import your custom components ===
// // Replace these paths with the actual paths in your project
// import Header from "../components/Header";
// import BlueButton from "../components/BlueButton";
// import SimpleTable from "../components/SimpleTable";
// import Card from "../components/Card";
// import Badge from "../components/Badge";
// import RetailerForm from "../components/RetailerForm";

// export default function Retailers() {
//   const [filter, setFilter] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [showAdd, setShowAdd] = useState(false);
//   const [list, setList] = useState([
//     { id: "RT-1001", name: "Maha Mobile Hub", code: "MHM001", status: "Active", balance: 12500, lastOrder: "2025-09-21" },
//     { id: "RT-1002", name: "Ganesh Telecom", code: "GNT021", status: "Inactive", balance: 3400, lastOrder: "2025-09-18" },
//     { id: "RT-1003", name: "Sai Krupa", code: "SAI077", status: "Active", balance: 0, lastOrder: "2025-09-22" },
//   ]);

//   const filtered = useMemo(
//     () =>
//       list.filter((r) =>
//         [r.name, r.code, r.id].join(" ").toLowerCase().includes(filter.toLowerCase())
//       ),
//     [filter, list]
//   );

//   const toggleStatus = (id) => {
//     setList((prev) =>
//       prev.map((r) =>
//         r.id === id ? { ...r, status: r.status === "Active" ? "Inactive" : "Active" } : r
//       )
//     );
//   };

//   const addRetailer = (payload) => {
//     const code =
//       (payload.name || "RTL")
//         .toUpperCase()
//         .replace(/[^A-Z]/g, "")
//         .slice(0, 3) +
//       String(Math.floor(100 + Math.random() * 900));
//     const id = `RT-${Math.floor(1000 + Math.random() * 9000)}`;
//     const item = { id, name: payload.name, code, status: "Active", balance: 0, lastOrder: "-" };
//     setList((prev) => [item, ...prev]);
//     setShowAdd(false);
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Retailers">
//         <div className="flex items-center gap-2">
//           <input
//             value={filter}
//             onChange={(e) => setFilter(e.target.value)}
//             placeholder="Search by name/code/id"
//             className="rounded-lg border px-2 py-1 text-sm"
//           />
//           <BlueButton onClick={() => setShowAdd(true)}>
//             <Plus className="mr-1 inline" size={14} /> Add Retailer
//           </BlueButton>
//         </div>
//       </Header>

//       <SimpleTable
//         headers={["Retailer (Code)", "Status", "Balance", "Last Order", "Actions"]}
//         rows={filtered.map((r) => [
//           <div key={r.id} className="font-medium">
//             {r.name} <span className="text-xs text-slate-500">({r.id} / {r.code})</span>
//           </div>,
//           <Badge key={`b-${r.id}`} label={r.status} color={r.status === "Active" ? "blue" : "red"} />,
//           `₹ ${r.balance.toLocaleString("en-IN")}`,
//           r.lastOrder,
//           <div className="flex flex-wrap gap-2" key={`act-${r.id}`}>
//             <BlueButton onClick={() => toggleStatus(r.id)}>
//               {r.status === "Active" ? "Deactivate" : "Activate"}
//             </BlueButton>
//             <BlueButton>Assign Keys</BlueButton>
//           </div>,
//         ])}
//       />

//       <Card title="Login / Password">
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
//           <input placeholder="Retailer ID" className="rounded-lg border px-3 py-2 text-sm" />
//           <input placeholder="Username" className="rounded-lg border px-3 py-2 text-sm" />
//           <div className="relative">
//             <input
//               type={showPwd ? "text" : "password"}
//               placeholder="New Password"
//               className="w-full rounded-lg border px-3 py-2 text-sm"
//             />
//             <button onClick={() => setShowPwd((s) => !s)} className="absolute right-2 top-2.5 text-slate-500">
//               {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//             </button>
//           </div>
//           <BlueButton className="sm:col-span-3">Update Password</BlueButton>
//         </div>
//       </Card>

//       {showAdd && (
//         <div className="rounded-2xl border bg-white p-4 shadow-xl">
//           <h3 className="text-sm font-semibold" style={{ color: "#0A3E91" }}>
//             Add Retailer
//           </h3>
//           <RetailerForm onCancel={() => setShowAdd(false)} onSubmit={addRetailer} />
//         </div>
//       )}
//     </div>
//   );
// }
